import { Box, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Range } from '../api/reports';

const DAY_MS = 86_400_000;

function day(value: Date): string {
  // Built from the local parts, not `toISOString()`: at 01:00 in Rome the UTC
  // date is still yesterday, and "today" would silently be the wrong day.
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const date = `${value.getDate()}`.padStart(2, '0');
  return `${value.getFullYear()}-${month}-${date}`;
}

function shift(from: Date, days: number): Date {
  return new Date(from.getTime() + days * DAY_MS);
}

export type RangePreset = 'today' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth';

/** The presets, as the range each one resolves to today. */
export function presetRange(preset: RangePreset, now: Date = new Date()): Range {
  switch (preset) {
    case 'today':
      return { from: day(now), to: day(now) };
    case 'last7':
      return { from: day(shift(now, -6)), to: day(now) };
    case 'thisMonth':
      return { from: day(new Date(now.getFullYear(), now.getMonth(), 1)), to: day(now) };
    case 'lastMonth': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: day(first), to: day(last) };
    }
    case 'last30':
    default:
      return { from: day(shift(now, -29)), to: day(now) };
  }
}

const PRESETS: RangePreset[] = ['today', 'last7', 'last30', 'thisMonth', 'lastMonth'];

/** Which preset, if any, a range is currently equal to. */
function matchingPreset(range: Range): RangePreset | null {
  return PRESETS.find((preset) => {
    const candidate = presetRange(preset);
    return candidate.from === range.from && candidate.to === range.to;
  }) ?? null;
}

interface RangePickerProps {
  value: Range;
  onChange: (range: Range) => void;
}

/**
 * The window every report is read through.
 *
 * Presets and two date fields together, rather than one or the other: "last 30
 * days" is what the office asks for nine times out of ten, and the month it
 * closes its books on is the tenth. Editing either field drops the preset
 * highlight instead of snapping back, so a half-typed custom window is not
 * fought over.
 */
export function RangePicker({ value, onChange }: RangePickerProps) {
  const { t } = useTranslation();
  const active = matchingPreset(value);

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      sx={{ alignItems: { md: 'center' } }}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={active}
        onChange={(_, preset: RangePreset | null) => {
          // A second click on the active button yields null; keep the window.
          if (preset) onChange(presetRange(preset));
        }}
        sx={{ flexWrap: 'wrap' }}
      >
        {PRESETS.map((preset) => (
          <ToggleButton key={preset} value={preset}>
            {t(`reports.range.presets.${preset}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          type="date"
          label={t('common.from')}
          value={value.from}
          onChange={(event) => onChange({ ...value, from: event.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 170 }}
        />
        <TextField
          type="date"
          label={t('common.to')}
          value={value.to}
          onChange={(event) => onChange({ ...value, to: event.target.value })}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ width: 170 }}
        />
      </Box>
    </Stack>
  );
}
